// @flow

import find from 'lodash/find';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import isArray from 'lodash/isArray';
import React, { Component } from 'react';
import ClassNames from 'classnames';
import { Carousel } from '@riseart/antd-provider';
import { application as CONFIG_APP } from 'Config';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { Wrapper } from '@riseart/layout';
import { isTouchDevice } from '@riseart/fe-utils';
import { QuizPage, quizPageVerticalCls } from 'shared_components/quiz/page/Page';
import { QuizInfoQuestion } from 'shared_components/quiz/common/questions/Info';
import { QuizSingleSelectQuestion } from 'shared_components/quiz/common/questions/SingleSelect';
import { QuizInputQuestion } from 'shared_components/quiz/common/questions/Text';
import QuizMultiSelectQuestion from 'shared_components/quiz/common/questions/MultiSelect';
import QuizImageSliderQuestion from 'shared_components/quiz/common/questions/ImageSlider';
import { QuizOverlay } from 'shared_components/quiz/overlay/Overlay';
import { ProgressBar } from 'shared_components/quiz/common/progressbar/ProgressBar';
import { WithOffsetHeight } from 'shared_hocs/gui/withOffsetHeight';
import { withGUIState } from 'shared_hocs/gui/withGuiState';
import { GUI_PROPERTIES } from 'shared_models/Gui';

import { raScreenSmMax, raScreenMdMax } from '@riseart/antd-provider/dist/website/variables.less';
import {
  quizContainerCls,
  quizContainerSliderCls,
  quizContainerSliderItemCls,
  quizContainerSliderVerticalCls,
  quizContainerSliderSingleSlideCls,
} from './Questions.less';

const HocProgressBar = withGUIState((state) => ({
  percent: state[GUI_PROPERTIES.QUIZ_PROGRESS],
}))(ProgressBar);

const VALIDATION_MAPPER = {
  regex: (answer: string, question: Object, { config, type, message }: Object): ?Object => {
    if (['number', 'string'].indexOf(typeof answer) < 0) {
      return null;
    }

    const re = new RegExp(config);
    const isValid = re.test(answer);

    return isValid
      ? null
      : {
          code: 'invalid',
          config,
          type,
          message,
        };
  },
  default: (answer: string | Array<*>, question: Object) => {
    const { required } = question;

    if (!required || (required && (answer.length || isNumber(answer)))) {
      return null;
    }

    return [{ code: 'required' }];
  },
};

const PARSE_REGEX = /{{(.*?)}}/g;
const DESTRUCT_PLACEHOLDER_REGEX = /{{question::(\d+).(.*)}}/;
const DEFAULT_REPLACEMENT = '_______';

/**
 * TYPE_TO_QUESTION_MAPPER
 *
 * Mapps question type to actual Question Component
 */
const TYPE_TO_QUESTION_MAPPER = {
  text: QuizInputQuestion,
  currency: QuizInputQuestion,
  display: QuizInfoQuestion,
  radio: QuizSingleSelectQuestion,
  checkbox: QuizMultiSelectQuestion,
  image: QuizImageSliderQuestion,
};

/**
 * replaceWith
 * Custom replacement function for matched placeholders in text
 *
 * @param {string} text
 * @param {string} matches
 * @param {string} replacement
 */
function replaceWith(text: string, matches: string, replacement: string = DEFAULT_REPLACEMENT) {
  const parsedValues = matches.match(DESTRUCT_PLACEHOLDER_REGEX);
  const hasMatch = parsedValues && parsedValues[0] && parsedValues[1];

  if (hasMatch) {
    return text.replace(parsedValues[0], replacement);
  }
}

type Props = {
  data: Object,
  visitorStoreCode: ?string,
  isLoading: boolean,
  onQuestionLoad: Function,
  onQuizSubmit: Function,
};

export const ITERATE_DIRECTIONS: Object = {
  next: 'next',
  previous: 'prev',
};

/**
 * QuizQuestionsContainer
 *
 * Container component to receive quizData and to render quiz page with quiz questions
 */
export class QuizQuestionsContainer extends Component<Props, any> {
  carouselRef: Array<Object> = [];

  previousSlideKey: number = -1;

  getSliderRefs: Function;

  handleQuestionSelect: Function;

  handleAfterChange: Function;

  handleKeyUp: Function;

  isTouchDevice: boolean;

  static defaultProps = {
    data: {},
    isLoading: false,
  };

  /**
   * constructor
   *
   * @param {*} props
   */
  constructor(props: Props) {
    super(props);
    this.state = {
      quizData: this.parsePlaceholders(this.props.data, replaceWith) || [],
      activeSection: this.getActiveSections(
        this.props.data,
        get(this.props.data, '[0].questions[0].id'),
      ),
    };
    this.isTouchDevice = isTouchDevice();
    this.props.onQuestionLoad(this.state.activeSection);
    this.bindMethods();
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    document.addEventListener('keyup', this.handleKeyUp);
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    document.removeEventListener('keyup', this.handleKeyUp);
  }

  /**
   * handleKeyUp
   * @param {Event} event
   * @returns {void}
   */
  handleKeyUp(event: Event): void {
    const ENTER_KEY = 'Enter';

    if (event.key !== ENTER_KEY || !this.isActiveAnswerValid()) {
      return;
    }

    this.handleQuestionSelect(this.state.activeSection.activePage, ITERATE_DIRECTIONS.next)();
  }

  /**
   * isActiveAnswerValid
   */
  isActiveAnswerValid() {
    const { activeSection } = this.state;
    const question = get(
      this.state.quizData,
      `[${activeSection.activePage}].questions[${activeSection.activeItemIndex}]`,
    );

    return !!(question && !question.error && !(question.required && !question.answer));
  }

  /**
   * bindMethods
   */
  bindMethods() {
    this.getSliderRefs = this.getSliderRefs.bind(this);
    this.handleQuestionSelect = this.handleQuestionSelect.bind(this);
    this.handleAfterChange = this.handleAfterChange.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleAnswer = this.handleAnswer.bind(this);
    this.handleNextSelectedQuestion = this.handleNextSelectedQuestion.bind(this);
  }

  /**
   * handleNextSelectedQuestion
   *
   * choose the next selected question based on passed questionId
   *
   * @param {number} questionId
   */
  handleNextSelectedQuestion(questionId: number) {
    const { activeSection, quizData } = this.state;

    if (activeSection.activeItem === questionId && activeSection.activeItemPosition > 0) {
      // Submit quiz questions data
      this.props.onQuizSubmit(this.state.quizData);
      return;
    }

    this.setState({ activeSection: this.getActiveSections(quizData, questionId) }, () =>
      this.props.onQuestionLoad(this.state.activeSection),
    );
  }

  /**
   * handleQuestionSelect
   *
   * @param {number} pageKey
   * @param {string} direction
   * @returns {Function}
   */
  handleQuestionSelect(pageKey: number, direction: string = ITERATE_DIRECTIONS.next): Function {
    return (): void => {
      const carousel = this.carouselRef[pageKey];

      // delay required to call next() and prev() of carousel, because for
      // some (mainly mobile) devices when a slide has a focus functionality like the textarea,
      // the focus() is messing up with the selected slide and does not position it properly
      const ANIMATION_DELAY_FIX = 300;
      if (direction === ITERATE_DIRECTIONS.next && this.isActiveAnswerValid()) {
        setTimeout(() => carousel.next(), ANIMATION_DELAY_FIX);
      } else if (direction === ITERATE_DIRECTIONS.previous) {
        setTimeout(() => carousel.prev(), ANIMATION_DELAY_FIX);
      }
    };
  }

  /**
   * handleAnswer
   * Handles raw data for the questions' answers and stores that in quizData state
   *
   * @param {Object} question
   * @param {string} answer
   */
  handleAnswer(question: Object, answer?: string = '') {
    const questionData = this.getQuestion(question.id, this.state.quizData);
    let error = VALIDATION_MAPPER.default(answer, questionData);

    if (questionData.validations) {
      const errorMessages = questionData.validations.reduce((result, rule) => {
        const validationHandler = VALIDATION_MAPPER[rule.type];
        const validationResult = validationHandler
          ? validationHandler(answer, questionData, rule)
          : null;

        if (validationResult) {
          result.push(validationResult);
        }

        return result;
      }, []);

      error = errorMessages.length ? errorMessages : null;
    }

    const validatedAnswers = this.handlerOptionValues(answer, questionData.options);
    const answerData = {
      questionId: questionData.id,
      [questionData.options ? 'options' : 'value']: validatedAnswers,
    };
    const quizData = [...this.state.quizData];
    const updatedQuestion = {
      ...questionData,
      error,
      answer: error ? null : answerData,
    };

    quizData[questionData.page].questions = quizData[questionData.page].questions.map((q) =>
      q.id !== updatedQuestion.id ? q : updatedQuestion,
    );

    // parse values in questions from answers
    this.setState({
      quizData: this.parsePlaceholders(quizData, this.replaceWithAnswer.bind(this)),
    });
  }

  /**
   * replaceWithAnswer
   * Replace matched placeholder, destruct it and gets the proper answer reference
   *
   * @param {string} text
   * @param {string} matches
   * @returns {string}
   */
  replaceWithAnswer(text: string, matches: string): string {
    const parsedValues = matches.match(DESTRUCT_PLACEHOLDER_REGEX);
    const hasMatch = parsedValues && parsedValues[0] && parsedValues[1];

    if (hasMatch) {
      const foundAnswer = get(
        this.getQuestion(parseInt(parsedValues[1], 10), this.state.quizData),
        `answer.${parsedValues[2]}`,
      );
      return text.replace(parsedValues[0], foundAnswer || DEFAULT_REPLACEMENT);
    }

    return text;
  }

  /**
   * getActiveSections
   *
   * @param {Array<Object>} haystack
   * @param {number} activeItemId
   * @returns {Object}
   */
  getActiveSections(haystack: Array<Object> = [], activeItemId: number): Object {
    const INITIAL_ACTIVESECTION = {
      activeItem: 0,
      activeItemIndex: 0,
      activeItemPosition: 0,
      activePage: 0,
      totalItems: 0,
    };

    return haystack.reduce(
      (result, page, idx) => {
        const totalQuestionsOnPage = page.questions.length;
        const questionIndex = page.questions.findIndex((q) => q.id === activeItemId);

        if (questionIndex !== -1) {
          result.activeItemPosition = (idx === 0 ? 0 : result.totalItems) + questionIndex;
        }

        result.totalItems += totalQuestionsOnPage;

        return questionIndex === -1
          ? result
          : {
              activeItem: activeItemId,
              activeItemIndex: questionIndex,
              activeItemPosition: result.activeItemPosition,
              activePage: idx,
              totalItems: result.totalItems,
            };
      },
      { ...INITIAL_ACTIVESECTION },
    );
  }

  /**
   * getQuestion
   *
   * Finds question in quizData by provided id
   *
   * @param {number} id
   * @param {Array<Object>} quizData
   * @returns {Object}
   */
  getQuestion(id: number, quizData: Array<Object>): Object {
    return quizData.reduce((result, i, k) => {
      const foundQuestion = find(i.questions, { id });
      return foundQuestion ? { ...foundQuestion, page: k } : result;
    }, {});
  }

  /**
   * handlerOptionValues
   *
   * @param {*} answer
   * @param {*} options
   * @returns {*}
   */
  handlerOptionValues(answer: any, options): any {
    if (!options || !answer) {
      return answer;
    }

    const optionsAnswers = isArray(answer) ? [...answer] : [answer];

    return optionsAnswers.reduce((res, a) => {
      const foundOption = find(options, ['id', a]);
      if (foundOption !== undefined) {
        res.push(foundOption.id);
      }

      return res;
    }, []);
  }

  /**
   * parsePlaceholders
   * Finds placeholders in quiz data questions and find matches that it will pass to the predicate (replacement function)
   *
   * @param {Array<Object.} quizData
   * @param {Function} predicate
   */
  parsePlaceholders(quizData: Array<Object> = [], predicate: Function = (text) => text) {
    return quizData.map((q) => ({
      ...q,
      questions: q.questions.map((question) => {
        const questionCopy = { ...question };
        const matched = questionCopy.text && questionCopy.text.match(PARSE_REGEX);

        if (!matched) {
          return questionCopy;
        }

        matched.forEach((m) => {
          questionCopy.text_parsed = predicate(questionCopy.text, m);
        });
        return questionCopy;
      }),
    }));
  }

  /**
   * handleAfterChange
   *
   * @param {*} pageKey
   * @returns {Function}
   */
  handleAfterChange(pageKey: number): Function {
    const nextPage = pageKey + 1;

    return (slideKey: number): void => {
      const { data } = this.props;
      const { activeSection } = this.state;
      const pageQuestions = data[pageKey].questions;
      let newQuestionId = pageQuestions[slideKey].id;

      if (
        (pageQuestions.length === 1 || slideKey === this.previousSlideKey) &&
        activeSection.activeItem === pageQuestions[pageQuestions.length - 1].id &&
        nextPage <= data.length - 1
      ) {
        // edge case when going to next question page (next carousel)
        newQuestionId = data[nextPage].questions[0].id;
        this.previousSlideKey = 0;
      } else if (pageKey > 0 && activeSection.activeItem === newQuestionId && slideKey === 0) {
        const { questions } = data[pageKey - 1];
        const lastQuestionIdx = questions.length - 1;
        // edge case when going to previous question page (next carousel)
        newQuestionId = questions[lastQuestionIdx].id;
        this.previousSlideKey = lastQuestionIdx;
      } else {
        this.previousSlideKey = slideKey;
      }

      this.handleNextSelectedQuestion(newQuestionId);
    };
  }

  /**
   * getSliderRefs
   *
   * @param {number} id
   * @returns {Function}
   */
  getSliderRefs(id: number): Function {
    return (ref) => {
      this.carouselRef[id] = ref;
    };
  }

  /**
   * calcInitialSlide
   *
   * @param {Object} currentPage
   * @param {number} iteratedPageIndex
   * @returns {number}
   */
  calcInitialSlide(currentPage: Object, iteratedPageIndex: number): number {
    const { activeSection } = this.state;

    if (activeSection.activePage === iteratedPageIndex) {
      return activeSection.activeItemIndex;
    }
    if (activeSection.activePage > iteratedPageIndex) {
      return currentPage.questions.length - 1;
    }

    return 0;
  }

  /**
   * getQuestionLabel
   * @param {Object} question
   * @returns {string}
   */
  getQuestionLabel(question: Object): ?string {
    const { type } = question;

    if (type === 'currency') {
      const { visitorStoreCode } = this.props;
      const [DEFAULT_CURRENCY_CODE] = Object.keys(CONFIG_APP.i18n.currency.signs);

      return CONFIG_APP.i18n.currency.signs[visitorStoreCode || DEFAULT_CURRENCY_CODE];
    }
  }

  /**
   * render
   *
   * @returns {React$Element<div>}
   */
  render() {
    const { data, isLoading } = this.props;
    const { activeSection } = this.state;
    const showOverlayHint =
      this.isTouchDevice &&
      activeSection &&
      get(
        data,
        `[${activeSection.activePage}].questions[${activeSection.activeItemIndex}].type`,
      ) === 'image';

    return (
      <WithOffsetHeight>
        {({ totalMessagesHeight }) => {
          return (
            <IsomorphicRipple isActive={isLoading}>
              <Wrapper
                sizes={{
                  xs: 'wide',
                  sm: 'wide',
                  md: 'wide',
                  lg: 'narrow',
                  xl: 'narrow',
                }}
              >
                <div className={quizContainerCls}>
                  {showOverlayHint && <QuizOverlay />}
                  <HocProgressBar />
                  {data.map((p, k) => (
                    <QuizPage
                      key={`quiz-page-${p.name}`}
                      pageNumber={k}
                      className={p.transition === 'scroll' ? quizPageVerticalCls : null}
                      isActive={k === activeSection.activePage}
                    >
                      <Carousel
                        ref={this.getSliderRefs(k)}
                        arrows={false}
                        dots={false}
                        infinite={false}
                        vertical={p.transition === 'scroll'}
                        centerMode
                        swipe={false}
                        centerPadding={p.transition === 'slide' ? 0 : '200px'}
                        adaptiveHeight
                        afterChange={this.handleAfterChange(k)}
                        initialSlide={this.calcInitialSlide(p, k)}
                        responsive={[
                          {
                            breakpoint: raScreenSmMax,
                            settings: {
                              centerPadding: 0,
                            },
                          },
                          {
                            breakpoint: raScreenMdMax,
                            settings: {
                              centerPadding: p.transition !== 'scroll' ? 0 : '270px',
                            },
                          },
                        ]}
                        className={ClassNames(quizContainerSliderCls, {
                          [quizContainerSliderVerticalCls]: p.transition === 'scroll',
                          [quizContainerSliderSingleSlideCls]: p.questions.length === 1,
                        })}
                      >
                        {p.questions.map((q, qIdx) => {
                          const QuizQuestionComponent = TYPE_TO_QUESTION_MAPPER[q.type];

                          if (!QuizQuestionComponent) {
                            return null;
                          }

                          const isFirstInList = qIdx === 0;
                          const buttons = {
                            back: find(q.buttons, { type: 'back', enabled: true }),
                            next: find(q.buttons, { type: 'next', enabled: true }),
                          };
                          const buttonsProps = {
                            backButton:
                              buttons.back && !isFirstInList
                                ? {
                                    translationText: buttons.back.text,
                                    backListener: this.handleQuestionSelect(
                                      k,
                                      ITERATE_DIRECTIONS.previous,
                                    ),
                                  }
                                : null,
                            nextButton: buttons.next
                              ? {
                                  translationText: buttons.next.text,
                                  nextListener: this.handleQuestionSelect(
                                    k,
                                    ITERATE_DIRECTIONS.next,
                                  ),
                                }
                              : null,
                          };

                          return (
                            <div key={q.id} className={quizContainerSliderItemCls}>
                              <QuizQuestionComponent
                                totalMessagesHeight={totalMessagesHeight}
                                isActive={activeSection.activeItem === q.id}
                                onChange={this.handleAnswer}
                                onSelectQuestion={this.handleQuestionSelect(k)}
                                hintText={q.helper}
                                required={q.required}
                                inputType={q.inputType}
                                order={q.order}
                                errors={q.error}
                                isValid={!!q.error || (q.required && !q.answer)}
                                {...buttonsProps}
                                {...q}
                                label={this.getQuestionLabel(q)}
                                text={q.text_parsed || q.text}
                              />
                            </div>
                          );
                        })}
                      </Carousel>
                    </QuizPage>
                  ))}
                </div>
              </Wrapper>
            </IsomorphicRipple>
          );
        }}
      </WithOffsetHeight>
    );
  }
}
